import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2, Truck, Wrench, ShoppingBag, Clock, Tag, AlertCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Branded line-item detail dialog.
 *
 * Customers tap any item on their quote and we resolve it via GET /api/catalogue/lookup
 * (by sku, falling back to name). Renders service, product or logistics-fee details in a
 * consistent, on-brand modal — yellow accent, dark teal shell, sharp corners.
 */
const KIND_META = {
  service: { icon: Wrench, label: "Service", chip: "bg-spark-yellow text-spark-ink" },
  product: { icon: ShoppingBag, label: "Part / Product", chip: "bg-spark-teal text-white" },
  logistics: { icon: Truck, label: "Logistics", chip: "bg-spark-teal-deep text-white" },
};

export const LineItemDetailDialog = ({ open, onClose, item }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setLoading(true);
    setErrored(false);
    setData(null);
    api.get("/catalogue/lookup", { params: { sku: item.sku || "", name: item.name || "" } })
      .then((r) => setData(r.data))
      .catch(() => setErrored(true))
      .finally(() => setLoading(false));
  }, [open, item]);

  if (!open) return null;
  const meta = data ? KIND_META[data.kind] : null;
  const Icon = meta?.icon || Tag;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="line-item-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-spark-teal/30 bg-spark-ink shadow-2xl overflow-hidden flex flex-col max-h-[88vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-spark-teal/10 border-b border-spark-teal/30 px-5 py-4 flex items-center gap-3">
          <Icon className="size-5 text-spark-teal" />
          <p className="font-display uppercase tracking-wider text-xs font-bold text-spark-teal flex-1">Quote line item</p>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-sm hover:bg-white/5" aria-label="Close" data-testid="line-item-close">
            <X className="size-4" />
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="size-6 animate-spin inline text-spark-yellow" /></div>
        ) : errored || !data ? (
          // Fallback — show whatever we have from the quote_items row itself.
          <div className="p-5 sm:p-6 text-slate-200" data-testid="line-item-fallback">
            <div className="flex items-start gap-2 text-spark-yellow mb-2"><AlertCircle className="size-4 mt-0.5 shrink-0" /><p className="text-xs">No extra details on file for this item &mdash; the price below is what we&apos;ll charge.</p></div>
            <h3 className="font-display uppercase tracking-tight text-xl font-bold text-white mb-1 break-words">{item?.name || "Line item"}</h3>
            <p className="font-display uppercase tracking-tight text-3xl font-bold text-spark-yellow">${Number(item?.price || 0).toFixed(2)}{item?.qty > 1 ? ` × ${item.qty}` : ""}</p>
          </div>
        ) : (
          <div className="overflow-y-auto" data-testid="line-item-loaded">
            {data.image_url && (
              <div className="aspect-[16/9] bg-spark-teal-night overflow-hidden border-b border-white/5">
                <img src={data.image_url} alt={data.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold ${meta?.chip}`}>
                  <Icon className="size-3" /> {meta?.label}
                </span>
                {data.sku && <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 font-mono">SKU · {data.sku}</span>}
                {data.kind === "product" && data.availability === "on_order" && (
                  <span className="inline-flex items-center gap-1 bg-yellow-200 text-spark-ink px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold">On order</span>
                )}
              </div>
              <h3 className="font-display uppercase tracking-tight text-xl sm:text-2xl font-bold text-white mb-2 break-words">{data.name}</h3>
              <p className="font-display uppercase tracking-tight text-3xl font-bold text-spark-yellow mb-4">${Number(data.price).toFixed(2)}{item?.qty > 1 ? ` × ${item.qty}` : ""}</p>
              {data.description && (
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap mb-4">{data.description}</p>
              )}
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-[11px] uppercase tracking-wider font-bold text-slate-400">
                {data.duration_minutes ? (
                  <span className="inline-flex items-center gap-1"><Clock className="size-3.5 text-spark-teal" /> ~{data.duration_minutes} min</span>
                ) : null}
                {data.category ? (
                  <span className="inline-flex items-center gap-1"><Tag className="size-3.5 text-spark-teal" /> {data.category}</span>
                ) : null}
              </div>
              {data.kind === "product" && data.slug && (
                <Link to={`/shop/${data.slug}`} target="_blank" className="mt-5 inline-flex items-center gap-1.5 text-spark-yellow hover:text-white text-xs uppercase tracking-wider font-bold" data-testid="line-item-shop-link">
                  See in shop <ExternalLink className="size-3" />
                </Link>
              )}
            </div>
          </div>
        )}

        <div className="px-5 py-3 bg-white/[0.03] border-t border-white/10 flex items-center justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-400" data-testid="line-item-ok">Got it</button>
        </div>
      </div>
    </div>
  );
};

export default LineItemDetailDialog;
